import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiPlus, FiTrash2, FiSave } = FiIcons;

const EditDataModal = ({ isOpen, onClose, section, data, onSave }) => {
  const [formData, setFormData] = useState(data);

  if (!isOpen) return null;

  const handleChange = (e, path = null) => {
    const { name, value, type } = e.target;
    let finalValue = value;
    if (type === 'number') finalValue = parseFloat(value) || 0;

    if (path) {
      // Handle deep nested updates (e.g., platforms.linkedin.impressions)
      const keys = path.split('.');
      setFormData(prev => {
        const newData = { ...prev };
        let current = newData;
        for (let i = 0; i < keys.length; i++) {
          if (i === keys.length - 1) {
            current[keys[i]] = { ...current[keys[i]], [name]: finalValue };
          } else {
            current[keys[i]] = { ...current[keys[i]] };
            current = current[keys[i]];
          }
        }
        return newData;
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: finalValue }));
    }
  };

  const handleArrayChange = (index, field, value, arrayName) => {
    const newArray = [...(formData[arrayName] || [])];
    newArray[index] = { ...newArray[index], [field]: value };
    setFormData(prev => ({ ...prev, [arrayName]: newArray }));
  };

  const addItem = (arrayName, emptyItem) => {
    setFormData(prev => ({ ...prev, [arrayName]: [...(prev[arrayName] || []), emptyItem] }));
  };

  const removeItem = (index, arrayName) => {
    setFormData(prev => ({ ...prev, [arrayName]: prev[arrayName].filter((_, i) => i !== index) }));
  };

  const renderFields = () => {
    switch (section) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
               <h3 className="font-bold text-blue-800 mb-3">Report Settings</h3>
               <Input 
                 label="Report Date Period" 
                 name="reportDate" 
                 type="date"
                 value={formData.reportDate || ''} 
                 onChange={handleChange} 
               />
            </div>

            <div>
               <h3 className="font-bold text-gray-700 mb-2">Executive Summary</h3>
               <TextArea 
                 label="Write your summary here..." 
                 name="executiveSummary" 
                 value={formData.executiveSummary || ''} 
                 onChange={handleChange}
                 rows={6}
               />
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-gray-700">Key Metrics</h3>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Total Traffic" name="traffic" value={formData.traffic} onChange={handleChange} type="number" />
                <Input label="Conversions" name="conversions" value={formData.conversions} onChange={handleChange} type="number" />
                <Input label="Revenue ($)" name="revenue" value={formData.revenue} onChange={handleChange} type="number" />
                <Input label="Tech Health" name="techHealth" value={formData.techHealth} onChange={handleChange} type="number" />
                <Input label="Backlinks" name="backlinks" value={formData.backlinks} onChange={handleChange} type="number" />
                <Input label="Active Keywords" name="activeKeywords" value={formData.activeKeywords} onChange={handleChange} type="number" />
              </div>
            </div>
          </div>
        );
      case 'seo':
        return (
          <div className="space-y-4">
            <h3 className="font-bold text-gray-700">SEO Metrics</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Traffic" name="traffic" value={formData.traffic} onChange={handleChange} type="number" />
              <Input label="Conversions" name="conversions" value={formData.conversions} onChange={handleChange} type="number" />
              <Input label="Tech Health" name="techHealth" value={formData.techHealth} onChange={handleChange} type="number" />
              <Input label="Backlinks" name="backlinks" value={formData.backlinks} onChange={handleChange} type="number" />
            </div>
            <ArraySection
              title="Top Keywords"
              items={formData.keywords}
              onAdd={() => addItem('keywords', { keyword: '', position: 0, change: 0, searches: 0, difficulty: 'Medium' })}
              onRemove={(i) => removeItem(i, 'keywords')}
              renderItem={(item, i) => (
                <>
                  <div className="flex-1"><Input label="Keyword" value={item.keyword} onChange={(e) => handleArrayChange(i, 'keyword', e.target.value, 'keywords')} /></div>
                  <div className="w-20"><Input label="Pos" type="number" value={item.position} onChange={(e) => handleArrayChange(i, 'position', parseFloat(e.target.value), 'keywords')} /></div>
                  <div className="w-20"><Input label="Vol" type="number" value={item.searches} onChange={(e) => handleArrayChange(i, 'searches', parseFloat(e.target.value), 'keywords')} /></div>
                </>
              )}
            />
          </div>
        );
      case 'gbp':
        return (
            <div className="space-y-4">
            <h3 className="font-bold text-gray-700">GBP Metrics</h3>
            <div className="grid grid-cols-2 gap-4">
                <Input label="Total Actions" name="actions" value={formData.actions} onChange={handleChange} type="number" />
                <Input label="Profile Views" name="views" value={formData.views} onChange={handleChange} type="number" />
                <Input label="Rating (0-5)" name="rating" value={formData.rating} onChange={handleChange} type="number" step="0.1" />
                <Input label="New Reviews" name="newReviews" value={formData.newReviews} onChange={handleChange} type="number" />
            </div>
            <ArraySection
                title="History (Jan-Jun)"
                items={formData.history}
                onAdd={() => addItem('history', { month: '', actions: 0, views: 0 })}
                onRemove={(i) => removeItem(i, 'history')}
                renderItem={(item, i) => (
                <>
                    <div className="w-24"><Input label="Month" value={item.month} onChange={(e) => handleArrayChange(i, 'month', e.target.value, 'history')} /></div>
                    <div className="flex-1"><Input label="Actions" type="number" value={item.actions} onChange={(e) => handleArrayChange(i, 'actions', parseFloat(e.target.value), 'history')} /></div>
                    <div className="flex-1"><Input label="Views" type="number" value={item.views} onChange={(e) => handleArrayChange(i, 'views', parseFloat(e.target.value), 'history')} /></div>
                </>
                )}
            />
            </div>
        );
      case 'googleAds':
        return (
            <div className="space-y-4">
            <h3 className="font-bold text-gray-700">Google Ads Metrics</h3>
            <div className="grid grid-cols-3 gap-4">
                <Input label="ROAS" name="roas" value={formData.roas} onChange={handleChange} type="number" step="0.1" />
                <Input label="Spend ($)" name="spend" value={formData.spend} onChange={handleChange} type="number" />
                <Input label="Conversions" name="conversions" value={formData.conversions} onChange={handleChange} type="number" />
                <Input label="Clicks" name="clicks" value={formData.clicks} onChange={handleChange} type="number" />
                <Input label="Cost/Conv ($)" name="costPerConv" value={formData.costPerConv} onChange={handleChange} type="number" step="0.01" />
                <Input label="CPC ($)" name="cpc" value={formData.cpc} onChange={handleChange} type="number" step="0.01" />
            </div>
            <ArraySection
                title="Campaigns"
                items={formData.campaigns}
                onAdd={() => addItem('campaigns', { name: '', spend: 0, conversions: 0, roas: 0 })}
                onRemove={(i) => removeItem(i, 'campaigns')}
                renderItem={(item, i) => (
                <>
                    <div className="flex-1"><Input label="Name" value={item.name} onChange={(e) => handleArrayChange(i, 'name', e.target.value, 'campaigns')} /></div>
                    <div className="w-24"><Input label="Spend" type="number" value={item.spend} onChange={(e) => handleArrayChange(i, 'spend', parseFloat(e.target.value), 'campaigns')} /></div>
                    <div className="w-20"><Input label="Conv" type="number" value={item.conversions} onChange={(e) => handleArrayChange(i, 'conversions', parseFloat(e.target.value), 'campaigns')} /></div>
                    <div className="w-20"><Input label="ROAS" type="number" value={item.roas} onChange={(e) => handleArrayChange(i, 'roas', parseFloat(e.target.value), 'campaigns')} /></div>
                </>
                )}
            />
            </div>
        );
      case 'metaAds':
        return (
            <div className="space-y-4">
            <h3 className="font-bold text-gray-700">Meta Ads Metrics</h3>
            <div className="grid grid-cols-3 gap-4">
                <Input label="ROAS" name="roas" value={formData.roas} onChange={handleChange} type="number" step="0.1" />
                <Input label="CTR (%)" name="ctr" value={formData.ctr} onChange={handleChange} type="number" step="0.1" />
                <Input label="Frequency" name="frequency" value={formData.frequency} onChange={handleChange} type="number" step="0.1" />
                <Input label="CPM ($)" name="cpm" value={formData.cpm} onChange={handleChange} type="number" step="0.01" />
                <Input label="Cost/Result" name="costPerResult" value={formData.costPerResult} onChange={handleChange} type="number" step="0.01" />
            </div>
            <ArraySection
                title="Ad Sets"
                items={formData.adSets}
                onAdd={() => addItem('adSets', { name: '', platform: 'Facebook', spend: 0, results: 0, roas: 0 })}
                onRemove={(i) => removeItem(i, 'adSets')}
                renderItem={(item, i) => (
                <>
                    <div className="flex-1"><Input label="Name" value={item.name} onChange={(e) => handleArrayChange(i, 'name', e.target.value, 'adSets')} /></div>
                    <div className="w-24"><Input label="Platform" value={item.platform} onChange={(e) => handleArrayChange(i, 'platform', e.target.value, 'adSets')} /></div>
                    <div className="w-20"><Input label="Spend" type="number" value={item.spend} onChange={(e) => handleArrayChange(i, 'spend', parseFloat(e.target.value), 'adSets')} /></div>
                    <div className="w-20"><Input label="ROAS" type="number" value={item.roas} onChange={(e) => handleArrayChange(i, 'roas', parseFloat(e.target.value), 'adSets')} /></div>
                </>
                )}
            />
            </div>
        );
      case 'youtube':
        return (
            <div className="space-y-4">
            <h3 className="font-bold text-gray-700">YouTube Metrics</h3>
            <div className="grid grid-cols-3 gap-4">
                <Input label="Views" name="views" value={formData.views} onChange={handleChange} type="number" />
                <Input label="View Rate (%)" name="viewRate" value={formData.viewRate} onChange={handleChange} type="number" step="0.1" />
                <Input label="Cost/View ($)" name="costPerView" value={formData.costPerView} onChange={handleChange} type="number" step="0.001" />
                <Input label="Completion (%)" name="completion" value={formData.completion} onChange={handleChange} type="number" step="1" />
                <Input label="Earned Subs" name="earnedSubs" value={formData.earnedSubs} onChange={handleChange} type="number" />
            </div>
            <ArraySection
                title="Campaigns"
                items={formData.campaigns}
                onAdd={() => addItem('campaigns', { name: '', views: 0, completion: 0, costPerView: 0 })}
                onRemove={(i) => removeItem(i, 'campaigns')}
                renderItem={(item, i) => (
                <>
                    <div className="flex-1"><Input label="Name" value={item.name} onChange={(e) => handleArrayChange(i, 'name', e.target.value, 'campaigns')} /></div>
                    <div className="w-20"><Input label="Views" type="number" value={item.views} onChange={(e) => handleArrayChange(i, 'views', parseFloat(e.target.value), 'campaigns')} /></div>
                    <div className="w-20"><Input label="Comp%" type="number" value={item.completion} onChange={(e) => handleArrayChange(i, 'completion', parseFloat(e.target.value), 'campaigns')} /></div>
                </>
                )}
            />
            </div>
        );
      case 'email':
        return (
            <div className="space-y-4">
            <h3 className="font-bold text-gray-700">Email Metrics</h3>
            <div className="grid grid-cols-3 gap-4">
                <Input label="List Size" name="listSize" value={formData.listSize} onChange={handleChange} type="number" />
                <Input label="Emails Sent" name="emailsSent" value={formData.emailsSent} onChange={handleChange} type="number" />
                <Input label="Open Rate (%)" name="openRate" value={formData.openRate} onChange={handleChange} type="number" step="0.1" />
                <Input label="Click Rate (%)" name="clickRate" value={formData.clickRate} onChange={handleChange} type="number" step="0.1" />
            </div>
            <ArraySection
                title="Campaigns"
                items={formData.campaigns}
                onAdd={() => addItem('campaigns', { name: '', type: 'Newsletter', sent: 0, openRate: 0, clickRate: 0 })}
                onRemove={(i) => removeItem(i, 'campaigns')}
                renderItem={(item, i) => (
                <>
                    <div className="flex-1"><Input label="Name" value={item.name} onChange={(e) => handleArrayChange(i, 'name', e.target.value, 'campaigns')} /></div>
                    <div className="w-20"><Input label="Sent" type="number" value={item.sent} onChange={(e) => handleArrayChange(i, 'sent', parseFloat(e.target.value), 'campaigns')} /></div>
                    <div className="w-16"><Input label="Open%" type="number" value={item.openRate} onChange={(e) => handleArrayChange(i, 'openRate', parseFloat(e.target.value), 'campaigns')} /></div>
                </>
                )}
            />
            </div>
        );
      case 'content':
        return (
          <div className="space-y-6">
            <h3 className="font-bold text-gray-700">Content Counts</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Blog Posts" name="blogPostsCount" value={formData.blogPostsCount} onChange={handleChange} type="number" />
              <Input label="Social Posts" name="socialPostsCount" value={formData.socialPostsCount} onChange={handleChange} type="number" />
            </div>

            <ArraySection
              title="Upcoming Blogs"
              items={formData.upcomingBlogs}
              onAdd={() => addItem('upcomingBlogs', { title: '', publishDate: '', status: 'draft', author: '' })}
              onRemove={(i) => removeItem(i, 'upcomingBlogs')}
              renderItem={(item, i) => (
                <>
                  <div className="flex-1"><Input label="Title" value={item.title} onChange={(e) => handleArrayChange(i, 'title', e.target.value, 'upcomingBlogs')} /></div>
                  <div className="w-32"><Input label="Date" type="date" value={item.publishDate} onChange={(e) => handleArrayChange(i, 'publishDate', e.target.value, 'upcomingBlogs')} /></div>
                  <div className="w-24"><Input label="Status" value={item.status} onChange={(e) => handleArrayChange(i, 'status', e.target.value, 'upcomingBlogs')} /></div>
                </>
              )}
            />

            <ArraySection
              title="Upcoming Social Posts"
              items={formData.upcomingSocial}
              onAdd={() => addItem('upcomingSocial', { content: '', platform: 'Facebook', publishDate: '', type: 'Educational' })}
              onRemove={(i) => removeItem(i, 'upcomingSocial')}
              renderItem={(item, i) => (
                <>
                  <div className="flex-1"><Input label="Content Snippet" value={item.content} onChange={(e) => handleArrayChange(i, 'content', e.target.value, 'upcomingSocial')} /></div>
                  <div className="w-24"><Input label="Platform" value={item.platform} onChange={(e) => handleArrayChange(i, 'platform', e.target.value, 'upcomingSocial')} /></div>
                  <div className="w-28"><Input label="Date" type="date" value={item.publishDate} onChange={(e) => handleArrayChange(i, 'publishDate', e.target.value, 'upcomingSocial')} /></div>
                </>
              )}
            />

            <ArraySection
              title="Upcoming Videos"
              items={formData.upcomingVideos}
              onAdd={() => addItem('upcomingVideos', { title: '', platform: 'YouTube', publishDate: '', duration: '' })}
              onRemove={(i) => removeItem(i, 'upcomingVideos')}
              renderItem={(item, i) => (
                <>
                  <div className="flex-1"><Input label="Title" value={item.title} onChange={(e) => handleArrayChange(i, 'title', e.target.value, 'upcomingVideos')} /></div>
                  <div className="w-24"><Input label="Platform" value={item.platform} onChange={(e) => handleArrayChange(i, 'platform', e.target.value, 'upcomingVideos')} /></div>
                   <div className="w-28"><Input label="Date" type="date" value={item.publishDate} onChange={(e) => handleArrayChange(i, 'publishDate', e.target.value, 'upcomingVideos')} /></div>
                </>
              )}
            />
          </div>
        );
      case 'social':
        return (
          <div className="space-y-6">
            {Object.keys(formData.platforms).map(platform => (
              <div key={platform} className="border border-gray-200 p-4 rounded-lg bg-gray-50">
                <h3 className="font-bold capitalize mb-2 text-gray-800">{platform}</h3>
                <div className="grid grid-cols-3 gap-3">
                  <Input label="Impressions" type="number" name="impressions" value={formData.platforms[platform].impressions} onChange={(e) => handleChange(e, `platforms.${platform}`)} />
                  <Input label="Engagement" type="number" name="engagement" value={formData.platforms[platform].engagement} onChange={(e) => handleChange(e, `platforms.${platform}`)} />
                  <Input label="Clicks" type="number" name="clicks" value={formData.platforms[platform].clicks} onChange={(e) => handleChange(e, `platforms.${platform}`)} />
                  <Input label="Followers" type="number" name="followers" value={formData.platforms[platform].followers} onChange={(e) => handleChange(e, `platforms.${platform}`)} />
                  <Input label="Posts" type="number" name="posts" value={formData.platforms[platform].posts} onChange={(e) => handleChange(e, `platforms.${platform}`)} />
                </div>
              </div>
            ))}
          </div>
        );
      default:
        return <div className="p-4 text-red-500">Unknown section: {section}</div>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
      >
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-xl">
          <h2 className="text-xl font-bold text-gray-800 capitalize">Edit {section === 'gbp' ? 'Google Business Profile' : section.replace(/([A-Z])/g, ' $1').trim()} Data</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FiX size={24} /></button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {renderFields()}
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end space-x-3 bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">Cancel</button>
          <button
            onClick={() => onSave(formData)}
            className="flex items-center space-x-2 px-6 py-2 bg-[#00A859] text-white rounded-lg hover:bg-[#008a4a] shadow-sm transition-all"
          >
            <SafeIcon icon={FiSave} />
            <span>Save Changes</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const Input = ({ label, ...props }) => (
  <div className="flex flex-col">
    <label className="text-xs text-gray-500 font-medium mb-1">{label}</label>
    <input className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#00A859] focus:outline-none w-full" {...props} />
  </div>
);

const TextArea = ({ label, ...props }) => (
    <div className="flex flex-col">
      <label className="text-xs text-gray-500 font-medium mb-1">{label}</label>
      <textarea className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#00A859] focus:outline-none w-full resize-y" {...props} />
    </div>
);

const ArraySection = ({ title, items, onAdd, onRemove, renderItem }) => (
  <div className="mt-6">
    <div className="flex justify-between items-center mb-2">
      <h3 className="font-bold text-gray-700">{title}</h3>
      <button onClick={onAdd} className="text-[#00A859] text-sm flex items-center font-medium hover:underline"><FiPlus className="mr-1" /> Add Item</button>
    </div>
    <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
      {items && items.map((item, i) => (
        <div key={i} className="flex gap-2 items-end bg-gray-50 p-3 rounded-lg border border-gray-100">
          {renderItem(item, i)}
          <button onClick={() => onRemove(i)} className="text-red-400 hover:text-red-600 p-2"><FiTrash2 /></button>
        </div>
      ))}
      {(!items || items.length === 0) && <p className="text-sm text-gray-400 italic">No items yet.</p>}
    </div>
  </div>
);

export default EditDataModal;